import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  credencialesInterceptor,
  proveerSesion,
  sesionExpiradaInterceptor,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { NavegacionService } from '@services/navegacion.service';
import { proveerI18n, proveerMigas, proveerOverlays } from '@intelishs-group/ui';
import { APLICACION_NOMINA } from '@schemas/roles';
import { EspacioService } from '@services/espacio.service';
import { proveerFuentesIrA } from '@services/ir-a-fuentes';
import { environment } from 'src/environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    // La hoja de cada ruta hereda los parametros y datos de sus padres: el
    // espacio, el nivel y los ids (`:empresa`, `:cliente`…) se leen de ahi.
    provideRouter(routes, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideBrowserGlobalErrorListeners(),
    // Los dos de la libreria de sesion. `credencialesInterceptor` va PRIMERO y es
    // obligatorio: sin `withCredentials` la cookie HttpOnly no viaja y todo da 401.
    // `sesionExpiradaInterceptor`: ante un 401 refresca UNA vez (compartido),
    // reintenta y, si no se pudo, avisa y sale al login; deja pasar `/sesion/*`.
    provideHttpClient(withInterceptors([credencialesInterceptor, sesionExpiradaInterceptor])),
    provideAnimationsAsync(),
    // El idioma de la plataforma (ui >= 5.2.10): registra `es-MX` y alinea con el a
    // Angular (`LOCALE_ID`) y a ng-zorro (`provideNzI18n` con `locale: 'es-MX'`).
    // Sustituye a las tres lineas a mano de antes (`registerLocaleData` de `es-MX` y
    // de `es`, `provideNzI18n(es_ES)` y `LOCALE_ID`): el date-picker formatea con el
    // `locale` del i18n de ng-zorro, que ahora tambien es `es-MX`.
    proveerI18n(),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin el,
    // desde Angular 21 un aviso de SweetAlert o el velo de carga lanzados con un modal
    // abierto salen DETRAS del modal, sin error en consola.
    proveerOverlays(),
    // Las migas de las pantallas que cuelgan de un area (`<intelishs-migas>` en su barra, ui >= 5.3.9): el
    // catalogo son las areas de la sesion con sus pantallas, de las mismas puertas que pintan sus tarjetas.
    proveerMigas(() => inject(NavegacionService).gruposMigas()),

    // La sesion del servicio de identidad de SINAY (sin login propio): la carga ANTES del
    // primer render y, si no hay una utilizable, avisa y manda al login central con
    // `?volverA=`. Hasta el 2026-10-23 esto era una fachada local (`src/app/auth/`, con su
    // `SESION_CONFIG` a mano, su inicializador y `sesionGuard`); las pantallas leen ahora
    // `SesionService` directo.
    proveerSesion({
      identidadUrl: environment.identidadUrl,
      // Se llama `logoutUrl` por historia (el boilerplate y los demas fronts), pero es el
      // login CENTRAL, a donde se va a INICIAR sesion. Va con su diagonal final: la libreria la
      // quita antes de agregar la suya (session-core >= 5.3.5).
      loginUrl: environment.logoutUrl,
      avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
      // Un acceso activo de OTRA aplicacion no es sesion de Nomina (session-core >= 5.3.4):
      // quien entro a Facturacion y abre un marcador de aqui va al login a elegir la tarjeta
      // de Nomina. nominaapi le responde 403 a esa sesion (desde el 2026-10-23); esto evita
      // que llegue a pedirlo.
      aplicacion: APLICACION_NOMINA,
      // Sin `rutaSinPermiso`: quien no tiene el modulo va a `/`, y `inicioGuard` lo lleva a SU
      // primera pantalla (como ffacturacion y fjuridico).
    }),

    // El espacio fija el nivel en cada navegacion: tiene que existir antes de la primera.
    provideAppInitializer(() => void inject(EspacioService)),

    // Lo que busca «Ir a…» (Ctrl+K), que monta el marco: pantallas por area, empresas,
    // clientes, nominas y trabajadores. En la RAIZ: provisto en una ruta no le llega.
    ...proveerFuentesIrA(),
  ],
};
